import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

import env from '@config/env';
import { logger } from '@utils/logger';
import type { User } from '@appTypes/auth';

// ── in-memory access token (never persisted to localStorage) ─────────────────
let _accessToken: string | null = null;
export const getAccessToken = () => _accessToken;
export const setAccessToken = (t: string | null) => {
  _accessToken = t;
};

export const apiClient = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: env.apiTimeout,
  withCredentials: true, // sends the httpOnly refresh cookie on every /auth/* call
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// Request interceptor — attach access token + scope headers
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (_accessToken) {
      config.headers.Authorization = `Bearer ${_accessToken}`;
    }
    // The main centre always travels with the request: it is what identifies an
    // "all centres" rollup (x-centre-id: all), and it resolves the shared GST
    // identity for receipts.
    const mainCentreId = localStorage.getItem('mainCentreId');
    if (mainCentreId) config.headers['x-main-centre-id'] = mainCentreId;
    const centreId = localStorage.getItem('centreId');
    if (centreId) config.headers['x-centre-id'] = centreId;
    const sessionId = localStorage.getItem('sessionId');
    if (sessionId) config.headers['x-session-id'] = sessionId;
    // For file uploads, drop the default JSON content-type so the browser sets
    // `multipart/form-data` with the correct boundary.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    logger.debug(`${config.method?.toUpperCase()} ${config.url}`, null, 'API');
    return config;
  },
  (error) => {
    logger.error('Request interceptor error', error, 'API');
    return Promise.reject(error);
  },
);

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token!);
  });
  failedQueue = [];
};

// Response interceptor — auto-refresh via httpOnly cookie on 401
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const isAuthCall =
      originalRequest.url?.includes('/auth/refresh') ||
      originalRequest.url?.includes('/auth/login');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthCall) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // No body — browser sends the httpOnly cookie automatically
        const { data } = await axios.post<{ accessToken: string; user: User }>(
          `${env.apiBaseUrl}/auth/refresh`,
          undefined,
          { withCredentials: true },
        );
        setAccessToken(data.accessToken);
        processQueue(null, data.accessToken);
        originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        setAccessToken(null);
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    logger.error(`API Error ${error.response?.status}`, error.response?.data, 'API');
    return Promise.reject(error);
  },
);
